// Thin async wrappers over the pure builders in pdokQueries.js. These do the
// actual network I/O (keyless PDOK Locatieserver) and are intentionally not
// unit-tested — the testable logic (URLs, paging, the >10k fallback decision)
// lives in pdokQueries.js.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (External data section)

import { buildLocationFromPdokDoc } from '../pdokLocation'
import {
    buildGemeenteSearchUrl,
    buildWijkListUrl,
    buildBuurtListUrl,
    buildAdresListUrl,
    parsePdokResponse,
    exceedsDeepPagingCap,
    buildStartOffsets,
} from './pdokQueries'

const getJson = async (url, signal) => {
    const res = await fetch(url, signal ? { signal } : undefined)
    if (!res.ok) throw new Error(`PDOK responded ${res.status}`)
    return res.json()
}

// Walk every page for a URL-builder bound to a fixed filter.
const pageAll = async (urlForStart, signal) => {
    const first = parsePdokResponse(await getJson(urlForStart(0), signal))
    let docs = [...first.docs]
    for (const start of buildStartOffsets(first.numFound).slice(1)) {
        const page = parsePdokResponse(await getJson(urlForStart(start), signal))
        docs = docs.concat(page.docs)
    }
    return { docs, numFound: first.numFound }
}

// Search cities (gemeenten) by name fragment.
const searchGemeenten = async (query, signal) => {
    if (!query || !query.trim()) return []
    const { docs } = parsePdokResponse(await getJson(buildGemeenteSearchUrl(query.trim()), signal))
    return docs
        .map((d) => ({ Naam: d.gemeentenaam || d.weergavenaam || '', GemeenteCode: d.gemeentecode || '' }))
        .filter((g) => g.Naam)
}

// All wijken in a city (paged).
const listWijken = async (gemeentenaam, signal) => {
    const { docs } = await pageAll((start) => buildWijkListUrl(gemeentenaam, { start }), signal)
    return docs
        .map((d) => ({
            Naam: d.wijknaam || d.weergavenaam || '',
            WijkCode: d.wijkcode || '',
            GemeenteCode: d.gemeentecode || '',
            GemeenteNaam: d.gemeentenaam || '',
        }))
        .filter((w) => w.WijkCode)
}

// All buurten in a wijk, for their names: addresses only carry the BuurtCode.
const listBuurten = async (wijkcode, signal) => {
    const { docs } = await pageAll((start) => buildBuurtListUrl(wijkcode, { start }), signal)
    return docs
        .map((d) => ({ BuurtCode: d.buurtcode || '', Naam: d.buurtnaam || d.weergavenaam || '' }))
        .filter((b) => b.BuurtCode)
}

// Every address in a wijk. When the wijk exceeds the deep-paging cap, page
// per-buurt instead (buurten are virtually always under the cap).
//
// Results stream: pass `onChunk` to receive each page's freshly-seen addresses
// as soon as it arrives, so callers can render the first results without waiting
// for the whole wijk to finish paging. The full deduped list is still returned.
const listAllAdressen = async ({ wijkcode }, signal, onChunk) => {
    // Shared accumulator so duplicates (an address pulled per-buurt can surface
    // twice) are dropped both in the streamed chunks and the final return.
    const byKey = new Map()
    const emit = (rawDocs) => {
        const fresh = []
        for (const doc of rawDocs) {
            const loc = buildLocationFromPdokDoc(doc)
            const key = loc.BAGid || `${loc.Postcode}-${loc.Huisnummer}-${loc.HuisnummerToevoeging}`
            if (key && !byKey.has(key)) {
                const item = { Key: key, ...loc }
                byKey.set(key, item)
                fresh.push(item)
            }
        }
        if (fresh.length && onChunk) onChunk(fresh)
    }

    // Page the remaining offsets of a fixed filter, emitting each page as it
    // lands. The first page is fetched by the caller (so it can read numFound).
    const pageRest = async (urlForStart, numFound) => {
        for (const start of buildStartOffsets(numFound).slice(1)) {
            emit(parsePdokResponse(await getJson(urlForStart(start), signal)).docs)
        }
    }

    const wijkUrl = (start) => buildAdresListUrl({ wijkcode, start })
    const first = parsePdokResponse(await getJson(wijkUrl(0), signal))
    emit(first.docs)

    if (!exceedsDeepPagingCap(first.numFound)) {
        await pageRest(wijkUrl, first.numFound)
        return Array.from(byKey.values())
    }

    // Over the deep-paging cap: the wijk-level walk can't reach past 10.000, so
    // enumerate the wijk's buurten and page each one instead (dedup drops the
    // overlap with the first wijk page already emitted above).
    const buurten = parsePdokResponse(await getJson(buildBuurtListUrl(wijkcode), signal)).docs
    for (const b of buurten) {
        const buurtUrl = (start) => buildAdresListUrl({ buurtcode: b.buurtcode, start })
        const firstB = parsePdokResponse(await getJson(buurtUrl(0), signal))
        emit(firstB.docs)
        await pageRest(buurtUrl, firstB.numFound)
    }

    return Array.from(byKey.values())
}

export { searchGemeenten, listWijken, listBuurten, listAllAdressen }
